import { ReactElement, useEffect, useRef, memo, MouseEventHandler } from 'react';
import { styled, Avatar, Box, Text } from '@cad-arc';
import { MapMode } from '../types';
import { MapView } from '../constructors';
import { useCurrentMapModeImage } from '../hooks/use-current-map-mode-image';
import { useEsriIdentityContext } from '../esri-identity-context';
import {
    findBasemapConfigForMode,
    isBasemapForConfig,
    loadBasemapForMode,
} from '../base-maps/helpers';
import { useEnabledFeatures } from '../../common/hooks/enabled-features/use-enabled-features';

//TODO: move colors to css
import { colors } from '~/colors/colors';

const MapModeToggleButton = styled('button')`
    position: absolute;
    bottom: 20px;
    left: 12px;
    border: none;
    background: none;
    margin: none;
    padding: none;
`;

type EsriToggleMapModeButtonProps = {
    theme: 'dark' | 'light';
    mapMode: MapMode;
    setMapMode: (newMapMode: MapMode) => void;
    mapView: MapView | undefined;
    embedded?: boolean;
    displayOnlyMode?: boolean;
    isAlternativeBasemap?: boolean;
    setMapLoadFailed?: (failed: boolean) => void;
    isFirstResponder?: boolean;
};

export const EsriToggleMapModeButton = memo(function EsriToggleMapModeButton({
    theme,
    mapMode,
    setMapMode,
    mapView,
    embedded,
    displayOnlyMode,
    isAlternativeBasemap,
    setMapLoadFailed,
    isFirstResponder,
}: EsriToggleMapModeButtonProps): ReactElement | null {
    const currentMapModeImage = useCurrentMapModeImage(mapMode, isAlternativeBasemap);
    const { refreshCredential, getEsriToken } = useEsriIdentityContext();
    const { mapTileCacheEnabled } = useEnabledFeatures();
    const keepMatchingBasemap = mapTileCacheEnabled === true;
    // Ref'd so the effect below doesn't re-run (and reload the basemap) just because
    // refreshCredential's identity churned for reasons unrelated to the current mode.
    const refreshCredentialRef = useRef(refreshCredential);
    refreshCredentialRef.current = refreshCredential;

    const defaultMode = theme === 'dark' ? 'DARK_MODE' : 'LIGHT_MODE';
    const defaultModeAlt = theme === 'dark' ? 'DARK_HUMAN_MODE' : 'LIGHT_HUMAN_MODE';

    const isDefaultMode = mapMode === defaultMode;
    const isDefaultModeAlt = mapMode === defaultModeAlt;

    useEffect(
        /**
         * Resolves the basemap config for the current `mapMode` (which basemap style is
         * shown - dark, light, satellite, or human-geography) and hands the actual
         * load/retry/swap to `loadBasemapForMode`, cancelling it on cleanup so a retry that
         * outlives this mode (it round-trips through a token refresh) can't land after a
         * newer mode toggle superseded it.
         */
        function loadBasemapOnModeChange() {
            const chosenBasemapConfiguration = findBasemapConfigForMode(
                mapMode,
                isAlternativeBasemap
            );

            if (!(chosenBasemapConfiguration?.id && mapView)) {
                return;
            }

            // cad.map_tile_cache.enabled: a view built on this mode's basemap already has it
            // (`useBasemapForMode` in createInitialWebMap). Loading it again would download a
            // second copy and redraw the map for nothing.
            if (
                keepMatchingBasemap &&
                isBasemapForConfig(mapView.map?.basemap, chosenBasemapConfiguration)
            ) {
                return;
            }

            const { cancelPendingLoadEffects } = loadBasemapForMode({
                mapView,
                basemapConfig: chosenBasemapConfiguration,
                isAlternativeBasemap,
                setMapLoadFailed,
                refreshCredential: () => refreshCredentialRef.current(),
                getEsriToken,
            });

            return cancelPendingLoadEffects;
        },
        [
            mapMode,
            mapView,
            isAlternativeBasemap,
            setMapLoadFailed,
            isFirstResponder,
            getEsriToken,
            keepMatchingBasemap,
        ]
    );

    // TODO: RND-33840 Shift+Click toggles between regular and human geography maps (test feature)
    //       Remove before deploy.
    const handleMapModeToggleButtonClick: MouseEventHandler<HTMLButtonElement> = (event) => {
        if (isDefaultMode && event.shiftKey) {
            setMapMode(defaultModeAlt);
        } else if (isDefaultModeAlt && event.shiftKey) {
            setMapMode(defaultMode);
        } else {
            setMapMode(isDefaultMode || isDefaultModeAlt ? 'SATELLITE_MODE' : defaultMode);
        }
    };

    return embedded || displayOnlyMode || !mapView ? null : (
        <MapModeToggleButton
            onClick={handleMapModeToggleButtonClick}
            aria-label={`Switch to ${
                isDefaultMode || isDefaultModeAlt ? 'Satellite' : 'Default'
            } mode`}
            data-testid="MAP_MODE_TOGGLE_BUTTON"
        >
            <Avatar aria-hidden name=" " src={currentMapModeImage} size="lg">
                <Box
                    height="100%"
                    width="100%"
                    position="absolute"
                    borderRadius="0px 0px 3px 3px"
                    background={`linear-gradient(180deg, ${colors.mapModeButtonGradientStart} 0%, ${colors.mapModeButtonGradientEnd} 100%)`}
                >
                    <Text
                        fontWeight="bold"
                        fontSize={14}
                        align="center"
                        casing="capitalize"
                        style={{
                            position: 'absolute',
                            bottom: '5px',
                            width: '100%',
                            color: colors.mapModeButtonDefaultText,
                        }}
                    >
                        {isDefaultMode || isDefaultModeAlt ? 'Satellite' : 'Default'}
                    </Text>
                </Box>
            </Avatar>
        </MapModeToggleButton>
    );
});
